import { pdf, DocumentProps } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import { useToast } from "@packages/contexts/ToastContext";
import { useState, useRef, useEffect } from "react";
import {
  FileText,
  Loader2,
  ChevronDown,
  Download,
  Settings,
  ChevronUp,
} from "lucide-react";
import { Button } from "@core/button";
import { useMobile } from "@packages/hooks/useMobile";
export default function PdfDownloadButton({
  document: pdfDocument,
  btnTheme,
  openCustomizationModal,
  pdfTitle = "Madlen",
  menuPosition = "bottom",
}: {
  document: React.ReactElement<DocumentProps>;
  btnTheme: string;
  openCustomizationModal?: () => void;
  pdfTitle: string;
  menuPosition?: "bottom" | "top";
}) {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useMobile();

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    }

    window.document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDownload = async () => {
    setIsLoading(true);
    try {
      const blob = await pdf(pdfDocument).toBlob();
      const url = URL.createObjectURL(blob);

      // Create temporary link and trigger download
      const link = window.document.createElement("a");
      link.href = url;
      link.download = `${pdfTitle}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);

      // Cleanup URL object
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("PDF generation error:", error);
      addToast({
        title: t("common.error"),
        description: t("common.errorDescription"),
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreview = async () => {
    setIsLoading(true);
    try {
      const blob = await pdf(pdfDocument).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (error) {
      console.error("PDF generation error:", error);
      addToast({
        title: t("common.error"),
        description: t("common.errorDescription"),
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex" ref={containerRef}>
      {/* Main download button */}
      <Button
        onClick={handleDownload}
        variant="solid"
        size="md"
        rounded="xl"
        loading={isLoading}
        bgColor={btnTheme}
        textColor="text-white"
        className={`hover:shadow-lg border-0 ${
          isMobile ? "" : "rounded-r-none"
        }`}
        leftIcon={
          isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download size={20} />
          )
        }
      >
        <span className="ml-2">{t("common.downloadPdf")}</span>
      </Button>
      {!isMobile && (
        <Button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          variant="solid"
          size="md"
          rounded="xl"
          bgColor={`${btnTheme} !bg-gradient-to-l`}
          textColor="text-white"
          className="hover:shadow-lg border-0 rounded-l-none border-l border-l-white/20 px-2"
        >
          {menuPosition === "bottom" ? (
            <ChevronDown size={16} />
          ) : (
            <ChevronUp size={16} />
          )}
        </Button>
      )}
      {/* Dropdown menu */}
      {isMenuOpen && !isMobile && (
        <div
          className={`absolute ${
            menuPosition === "bottom"
              ? "right-0 top-full mt-1"
              : "left-0 bottom-full mb-1"
          } bg-white dark:bg-gray-800 shadow-lg rounded-lg z-10 w-48 py-1 border border-gray-200 dark:border-gray-700`}
        >
          {openCustomizationModal && (
            <button
              onClick={openCustomizationModal}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Settings size={16} className="mr-2" />
              {t("pdfCustomization.customizePdf")}
            </button>
          )}
          <button
            onClick={handlePreview}
            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <FileText size={16} className="mr-2" />
            {t("pdfCustomization.preview")}
          </button>
        </div>
      )}
    </div>
  );
}
